import React from 'react';
import * as ReactDOM from 'react-dom/client';
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles';
import ReduxToastr from 'react-redux-toastr';
import { Provider } from 'react-redux';
import { Spinner } from 'react-redux-spinner';
import { CustomProvider } from 'rsuite';
import frFR from 'rsuite/locales/fr_FR';
import enUS from 'rsuite/locales/en_US';
import './styles/shared.scss';
import { HistoryRouter as Router } from 'redux-first-history/rr6';
// eslint-disable-next-line import/no-unresolved
import ErrorBoundary, { reportUnhandledAsyncErrors } from '@commonClient2/components/ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { store, history } from './store';
import Routes from './routes';
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';
import 'react-redux-spinner/dist/react-redux-spinner.css';
import 'rsuite/dist/rsuite.min.css';
import '@commonClient2/style.scss';
import i18n from './services/i18n';

const theme = createTheme({
    palette: {
        primary: {
            main: '#3366FF',
        },
        secondary: {
            main: '#D7D7D7',
        },
    },
    overrides: {
        MuiAccordion: {
            root: {
                minWidth: '75%',
                maxWidth: '75%',
            },
        },
        MuiAccordionSummary: {
            content: {
                alignItems: 'center',
            },
        },
        MuiTextField: {
            root: {
                width: '100%',
            },
        },
        MuiFormControl: {
            root: {
                backgroundColor: 'white',
            },
        },
        MuiAppBar: {
            colorPrimary: {
                backgroundColor: '#FFFFFF',
                color: '#000000',
            },
        },
        MuiButton: {
            contained: {
                minWidth: '100px',
                color: '#FFFFFF',
                backgroundColor: '#0A54A2',
                boxShadow: '0px 0px 12px #0A54A229',
                borderRadius: '4px',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                padding: '6px 16px',
                opacity: '1',
                '&:hover': {
                    backgroundColor: '#2265AB',
                },
                margin: '10px 3px 10px 5px',
                height: '46px',
            },
        },
    },
});

// Component to dynamically provide RSuite locale based on current language
const RsuiteLocaleProvider = ({ children }) => {
    const [locale, setLocale] = React.useState(i18n.language.includes('fr') ? frFR : enUS);

    React.useEffect(() => {
        const handleLanguageChange = (lng) => {
            setLocale(lng.includes('fr') ? frFR : enUS);
        };

        i18n.on('languageChanged', handleLanguageChange);

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);

    return <CustomProvider locale={locale}>{children}</CustomProvider>;
};

/** @type {HTMLElement} */
const rootElement = document.querySelector('#root') || document.createElement('DIV');
const root = ReactDOM.createRoot(rootElement);

root.render(
    <React.StrictMode>
        <ErrorBoundary>
            <Provider store={store}>
                <Router history={history}>
                    <RsuiteLocaleProvider>
                        <MuiThemeProvider theme={theme}>
                            {/* <MuiPickersUtilsProvider utils={DateFnsUtils}> */}
                            <Spinner config={{ easing: 'ease', speed: 1000 }} />
                            <Routes />
                            <ReduxToastr
                                timeOut={4000}
                                newestOnTop={false}
                                preventDuplicates
                                position="top-center"
                                transitionIn="fadeIn"
                                transitionOut="fadeOut"
                                closeOnToastrClick
                            />
                            {/* </MuiPickersUtilsProvider> */}
                        </MuiThemeProvider>
                    </RsuiteLocaleProvider>
                </Router>
            </Provider>
        </ErrorBoundary>
    </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// eslint-disable-next-line no-console
reportWebVitals(console.log);
reportUnhandledAsyncErrors();
